import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
} from "@mui/material";
import dayjs from "dayjs";
import { apiFetch, API_BASE } from "./utils/apiClient";
import { toDateSafe } from "./utils/toDateSafe";

// ✅ "مياسة" أونلاين - مو ضمن رصيد/إدارة الإجازات هذا (لسه تكدر تطلب إجازة من
// رابطها الخاص، بس ما تظهر ببطاقات الرصيد ولا بفلتر الموظف هنا)
const EMPLOYEES = ["بنين", "محمد", "عبد الرحمن"];

// ⚠️ حساب art@1234 كان يسجل الإجازات/الغياب باسم "الورشة" (تسمية قسم وليس
// موظف) قبل ما نصححه لعبد الرحمن - هذا alias يربط السجلات القديمة المخزّنة
// بالاسم القديم برصيد عبد الرحمن بدون الحاجة لتعديل البيانات القديمة يدوياً
const EMPLOYEE_ALIASES = { "الورشة": "عبد الرحمن" };
const normalizeEmployee = (raw) => EMPLOYEE_ALIASES[raw] || raw;

// 🚫 موظفين ما ينحسب إلهم غياب ولا أوفر تايم بهاي الواجهة (بطلب المستخدم:
// بنين) - ما تظهر عندهم أرقام الغياب/الأوفر تايم بالبطاقة ولا يظهرون
// بقائمة الموظف بنموذج إضافة غياب/أوفر تايم. سجلاتهم القديمة تبقى بقائمة الحركات
const NO_ABSENCE_OVERTIME = ["بنين"];
const ABSENCE_OVERTIME_EMPLOYEES = EMPLOYEES.filter((e) => !NO_ABSENCE_OVERTIME.includes(e));

// 💼 رصيد الإجازات الأساسي لكل موظف (بالأيام) - يُضاف عليه التراكم الشهري
const BASE_LEAVE_BALANCE = {
  "بنين": 17,
  "محمد": 15.75,
  "عبد الرحمن": 15.75,
};

const STATUS_LABEL = {
  pending: "قيد المراجعة",
  approved: "موافقة",
  rejected: "مرفوض",
};

const statusColor = (s) => {
  if (s === "approved") return "success";
  if (s === "rejected") return "error";
  return "warning";
};

// 🗓️ نظام تراكم رصيد الإجازات (الإجازات المستحقة): يوم و6 ساعات يُضاف
// تلقائياً أول كل شهر ميلادي بدءاً من 1/9/2026 (محسوب مباشرة من التاريخ -
// بدون الحاجة لأي مهمة مجدولة/كرون)
const ACCRUAL_START = dayjs("2026-09-01");
const ACCRUAL_PER_MONTH = 1.75; // يوم واحد + 6 ساعات (6 × 0.125 = 0.75 يوم)
// ⏰ كل ساعة إجازة = 0.125 يوم (يعني يوم الإجازة = 8 ساعات) - هذا هو معامل
// التحويل المعتمد لخصم الإجازات الزمنية من الرصيد (بطلب المستخدم بالضبط)
const HOURLY_LEAVE_DAY_FRACTION = 0.125;

const monthsAccrued = (today) => {
  if (today.isBefore(ACCRUAL_START, "day")) return 0;
  return today.diff(ACCRUAL_START, "month") + 1;
};

const formatDaysHours = (days, hours) => {
  const parts = [];
  if (days) parts.push(`${days} يوم`);
  if (hours) parts.push(`${formatNum(hours)} ساعة`);
  return parts.length ? parts.join(" و ") : "0";
};

const formatNum = (n) => {
  const v = Number(n || 0);
  return Number.isInteger(v) ? `${v}` : v.toFixed(2).replace(/\.?0+$/, "");
};

// 🗓️ رصيد الإجازات (الكلي والمتبقي) يُعرض بصيغة "يوم و ساعة" بدل رقم عشري -
// نفس معامل التحويل المعتمد بالأعلى (كل 0.125 يوم = ساعة، يعني يوم الإجازة
// = 8 ساعات) فمثلاً 12.5 يوم = 12 يوم و 4 ساعات، و12.75 يوم = 12 يوم و 6 ساعات
const formatBalanceDaysHours = (decimalDays) => {
  const v = Number(decimalDays || 0);
  const neg = v < 0;
  const abs = Math.abs(v);
  const days = Math.floor(abs + 1e-9);
  const hours = Math.round(((abs - days) / HOURLY_LEAVE_DAY_FRACTION) * 100) / 100;
  const text = formatDaysHours(days, hours);
  return neg ? `-${text}` : text;
};

const formatTime12h = (t) => {
  if (!t) return "-";
  const parsed = dayjs(t, "HH:mm");
  return parsed.isValid() ? parsed.format("hh:mm A") : t;
};

// 💰 أجر ساعة الأوفر تايم (دينار) - لمحمد وعبد الرحمن بس (بطلب المستخدم
// بالضبط)؛ بنين تسجل ساعاتها بس بدون احتساب أجر
const OVERTIME_HOURLY_RATE = {
  "محمد": 3750,
  "عبد الرحمن": 3750,
};

const formatMoney = (n) => `${Math.round(Number(n || 0)).toLocaleString("en-US")} د.ع`;

// 🗓️ تاريخ/وقت تقديم الحركة فعلياً (createdAt) - يختلف عن تاريخ الإجازة/
// الغياب/الأوفر تايم نفسه، وهذا اللي يهم لمعرفة شوكت انسجلت الحركة
const formatSubmittedAt = (ts) => {
  const d = toDateSafe(ts);
  if (!d) return "-";
  return dayjs(d).format("YYYY-MM-DD hh:mm A");
};

// ⏰ خيارات دروب داون الساعة (بدل منتقي وقت تعبان) - من 9 الصبح إلى 11
// بليل بفواصل ربع ساعة (00/15/30/45) حتى تكدرين تختارين وقت مثل 2:15 أو
// 2:30 - نفس الخيارات لإضافة إجازة/غياب/أوفر تايم
const HOUR_OPTIONS = [];
for (let hour = 9; hour <= 23; hour++) {
  for (let minute = 0; minute < 60; minute += 15) {
    HOUR_OPTIONS.push(`${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`);
  }
}

const formatHourOption = (t) => {
  const parsed = dayjs(t, "HH:mm");
  return parsed.isValid() ? parsed.format("h:mm A") : t;
};

// عدد الساعات بين وقتين - "إلى" لازم يكون بعد "من" بنفس اليوم (بدون عبور
// منتصف الليل)، فأي وقت "إلى" أصغر أو يساوي "من" يعتبر غير صالح = 0 ساعة
const hoursBetween = (from, to) => {
  if (!from || !to) return 0;
  const [fH, fM] = from.split(":").map(Number);
  const [tH, tM] = to.split(":").map(Number);
  const minutes = tH * 60 + tM - (fH * 60 + fM);
  if (minutes <= 0) return 0;
  return Math.round((minutes / 60) * 4) / 4;
};

// 🕐 حقلي "من الساعة" / "إلى الساعة" بدروب داون بسيط - مشتركة بين نماذج
// إضافة إجازة/غياب/أوفر تايم حتى ما نكرر نفس الكود بالثلاثة أماكن. خيارات
// "إلى الساعة" تنحصر بالساعات اللي بعد "من الساعة" المختارة فقط
function HourRangeFields({ fromValue, toValue, onFromChange, onToChange }) {
  const toOptions = HOUR_OPTIONS.filter((t) => !fromValue || t > fromValue);
  return (
    <>
      <Grid item xs={6} sm={3}>
        <FormControl fullWidth size="small">
          <InputLabel sx={{ right: 0, left: "auto" }}>من الساعة</InputLabel>
          <Select
            label="من الساعة"
            value={fromValue || ""}
            onChange={(e) => {
              const v = e.target.value;
              onFromChange(v);
              if (toValue && toValue <= v) onToChange("");
            }}
          >
            {HOUR_OPTIONS.map((t) => (
              <MenuItem key={t} value={t}>{formatHourOption(t)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={6} sm={3}>
        <FormControl fullWidth size="small" disabled={!fromValue}>
          <InputLabel sx={{ right: 0, left: "auto" }}>إلى الساعة</InputLabel>
          <Select
            label="إلى الساعة"
            value={toValue || ""}
            onChange={(e) => onToChange(e.target.value)}
          >
            {toOptions.map((t) => (
              <MenuItem key={t} value={t}>{formatHourOption(t)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </>
  );
}

export default function LeaveAdmin() {
  const [requests, setRequests] = useState([]);
  const [overtimeRecords, setOvertimeRecords] = useState([]);
  const [absenceRecords, setAbsenceRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const [employeeFilter, setEmployeeFilter] = useState("الكل");
  const [statusFilter, setStatusFilter] = useState("الكل");
  const [kindFilter, setKindFilter] = useState("الكل");
  const [searchText, setSearchText] = useState("");

  // 🧾 نموذج "إضافة حركة" الموحد - بدل ما تكون الثلاث نماذج (غياب/إجازة/
  // أوفر تايم) ظاهرة كلها بنفس الوقت وتاخذ مساحة، نختار النوع أول من زر
  // ثلاثي وبعدها يظهر النموذج المطلوب بس
  const [addFormType, setAddFormType] = useState(null); // null | "absence" | "leave" | "overtime"

  // ✍️ نموذج إضافة غياب
  const [absEmployee, setAbsEmployee] = useState(ABSENCE_OVERTIME_EMPLOYEES[0]);
  const [absDate, setAbsDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [absType, setAbsType] = useState("fullDay"); // fullDay | hourly
  const [absFromTime, setAbsFromTime] = useState("");
  const [absToTime, setAbsToTime] = useState("");
  const [absReason, setAbsReason] = useState("");
  const [absSaving, setAbsSaving] = useState(false);

  // ✍️ نموذج إضافة إجازة (المدير يسجلها مباشرة بحالة "موافقة" - وبأي
  // تاريخ حتى لو قديم، بدون قيد "ممنوع تاريخ قديم" المفروض على الموظف
  // بصفحة طلب الإجازة الذاتية)
  const [leaveEmployee, setLeaveEmployee] = useState(EMPLOYEES[0]);
  const [leaveDate, setLeaveDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [leaveType, setLeaveType] = useState("fullDay"); // fullDay | hourly
  const [leaveFromTime, setLeaveFromTime] = useState("");
  const [leaveToTime, setLeaveToTime] = useState("");
  const [leaveReason, setLeaveReason] = useState("");
  const [leaveSaving, setLeaveSaving] = useState(false);

  // ✍️ نموذج إضافة / تعديل أوفر تايم (انتقل من سستم الحسابات لهنا مع
  // باقي حركات الموظف)
  const [otEmployee, setOtEmployee] = useState(ABSENCE_OVERTIME_EMPLOYEES[0]);
  const [otDate, setOtDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [otFromTime, setOtFromTime] = useState("");
  const [otToTime, setOtToTime] = useState("");
  const [otReason, setOtReason] = useState("");
  const [otSaving, setOtSaving] = useState(false);
  const [otEditingId, setOtEditingId] = useState(null);

  // 🙈 إخفاء موظف (مثلاً ترك الشغل) - من صلاحية المدير فقط (الصفحة نفسها
  // admin-only والـAPI محمي بـrequireAdmin). الإخفاء بصري فقط: بياناته تبقى
  // وتكدرين ترجعين تشوفينها من زر "عرض المخفيين" وترجعينه بـ"إظهار"
  const [hiddenEmployees, setHiddenEmployees] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const [hideDialogOpen, setHideDialogOpen] = useState(false);

  const isHidden = (emp) => hiddenEmployees.includes(emp);
  const visibleEmployees = EMPLOYEES.filter((e) => !isHidden(e));
  const cardEmployees = showHidden ? EMPLOYEES : visibleEmployees;
  const absEmployeeOptions = ABSENCE_OVERTIME_EMPLOYEES.filter((e) => !isHidden(e));
  const otEmployeeOptions = absEmployeeOptions;

  // لو الموظف المختار بنموذج الإضافة صار مخفي (أو ما يحق له غياب/أوفر
  // تايم) نرجّع الاختيار لأول موظف متاح - تعديل أوفر تايم قديم يبقى على موظفه
  useEffect(() => {
    if (!absEmployeeOptions.includes(absEmployee)) setAbsEmployee(absEmployeeOptions[0] || "");
    if (!visibleEmployees.includes(leaveEmployee)) setLeaveEmployee(visibleEmployees[0] || "");
    if (!otEditingId && !otEmployeeOptions.includes(otEmployee)) setOtEmployee(otEmployeeOptions[0] || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenEmployees, otEditingId]);

  const currentUser = localStorage.getItem("username") || "مدير";

  const fetchHiddenEmployees = async () => {
    try {
      const res = await apiFetch(`${API_BASE}/hr/hidden-employees`);
      const data = await res.json();
      if (res.ok) setHiddenEmployees(data.hiddenEmployees || []);
    } catch (err) {
      console.error("Error fetching hidden employees:", err);
    }
  };

  const toggleEmployeeHidden = async (emp) => {
    const hide = !isHidden(emp);
    if (hide && !window.confirm(`تأكيد إخفاء ${emp}؟ (بياناته تبقى وتكدر ترجعه بأي وقت)`)) return;
    try {
      const res = await apiFetch(`${API_BASE}/hr/hidden-employees`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ employee: emp, hidden: hide }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "فشل تحديث الإخفاء");
      setHiddenEmployees(data.hiddenEmployees || []);
      if (!(data.hiddenEmployees || []).length) setShowHidden(false);
      if (hide && employeeFilter === emp && !showHidden) setEmployeeFilter("الكل");
    } catch (err) {
      console.error("Error toggling employee hidden:", err);
      window.alert("حدث خطأ أثناء إخفاء/إظهار الموظف");
    }
  };

  const fetchData = async () => {
    setLoading(true);

    const [leaveRes, otRes, absRes] = await Promise.all([
      apiFetch(`${API_BASE}/hr/leave-requests`),
      apiFetch(`${API_BASE}/hr/overtime`),
      apiFetch(`${API_BASE}/hr/absences`),
      fetchHiddenEmployees(),
    ]);

    const [leaveData, otData, absData] = await Promise.all([
      leaveRes.json(),
      otRes.json(),
      absRes.json(),
    ]);

    if (!leaveRes.ok || !otRes.ok || !absRes.ok) {
      console.error("Error fetching HR data:", leaveData?.error || otData?.error || absData?.error);
      setLoading(false);
      return;
    }

    const data = [...leaveData];
    data.sort((a, b) => (toDateSafe(b.createdAt)?.getTime() || 0) - (toDateSafe(a.createdAt)?.getTime() || 0));
    setRequests(data);

    setOvertimeRecords(otData);
    setAbsenceRecords(absData);

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const absHours = useMemo(
    () => (absType === "hourly" ? hoursBetween(absFromTime, absToTime) : 0),
    [absType, absFromTime, absToTime]
  );

  const addAbsenceRecord = async () => {
    if (!absEmployee) {
      window.alert("اختاري الموظف");
      return;
    }
    if (!absDate) {
      window.alert("اختاري التاريخ");
      return;
    }
    if (absType === "hourly" && (!absFromTime || !absToTime || absHours <= 0)) {
      window.alert("حددي وقت البداية والنهاية بشكل صحيح (لازم إلى الساعة تكون بعد من الساعة)");
      return;
    }
    if (!absReason.trim()) {
      window.alert("لازم تكتبين السبب / الملاحظات");
      return;
    }

    try {
      setAbsSaving(true);

      const res = await apiFetch(`${API_BASE}/hr/absences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          employee: absEmployee,
          date: absDate,
          type: absType,
          fromTime: absType === "hourly" ? absFromTime : null,
          toTime: absType === "hourly" ? absToTime : null,
          hours: absType === "hourly" ? absHours : 0,
          reason: absReason.trim(),
          createdBy: currentUser,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "فشل حفظ الغياب");
      }

      await fetchData();
      setAbsFromTime("");
      setAbsToTime("");
      setAbsReason("");
    } catch (err) {
      console.error("Error saving absence record:", err);
      window.alert("حدث خطأ أثناء حفظ الغياب");
    } finally {
      setAbsSaving(false);
    }
  };

  const leaveHours = useMemo(
    () => (leaveType === "hourly" ? hoursBetween(leaveFromTime, leaveToTime) : 0),
    [leaveType, leaveFromTime, leaveToTime]
  );

  // ➕ إضافة إجازة مباشرة من المدير - تُسجَّل بحالة "موافقة" فوراً (مو
  // "قيد المراجعة") لأنها مسجّلة مباشرة من الإدارة مو طلب ذاتي، وتقبل أي
  // تاريخ (حتى قديم) خلافاً لصفحة طلب الإجازة الذاتية للموظف
  const addLeaveRecord = async () => {
    if (!leaveEmployee) {
      window.alert("اختاري الموظف");
      return;
    }
    if (!leaveDate) {
      window.alert("اختاري التاريخ");
      return;
    }
    if (leaveType === "hourly" && (!leaveFromTime || !leaveToTime || leaveHours <= 0)) {
      window.alert("حددي وقت البداية والنهاية بشكل صحيح (لازم إلى الساعة تكون بعد من الساعة)");
      return;
    }
    if (!leaveReason.trim()) {
      window.alert("لازم تكتبين السبب / الملاحظات");
      return;
    }

    try {
      setLeaveSaving(true);

      const res = await apiFetch(`${API_BASE}/hr/leave-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          employee: leaveEmployee,
          date: leaveDate,
          type: leaveType,
          fromTime: leaveType === "hourly" ? leaveFromTime : null,
          toTime: leaveType === "hourly" ? leaveToTime : null,
          hours: leaveType === "hourly" ? leaveHours : 0,
          reason: leaveReason.trim(),
          createdBy: currentUser,
          isAdminDirect: true,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "فشل حفظ الإجازة");
      }

      await fetchData();
      setLeaveFromTime("");
      setLeaveToTime("");
      setLeaveReason("");
    } catch (err) {
      console.error("Error saving leave record:", err);
      window.alert("حدث خطأ أثناء حفظ الإجازة");
    } finally {
      setLeaveSaving(false);
    }
  };

  const removeAbsence = async (id) => {
    const ok = window.confirm("تأكيد حذف سجل الغياب؟");
    if (!ok) return;
    await apiFetch(`${API_BASE}/hr/absences/${id}`, { method: "DELETE" });
    setAbsenceRecords((prev) => prev.filter((x) => x.id !== id));
  };

  const markOvertimePaid = async (id) => {
    await apiFetch(`${API_BASE}/hr/overtime/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        paid: true,
        paidAt: true,
        paidBy: currentUser,
      }),
    });
    setOvertimeRecords((prev) => prev.map((r) => (r.id === id ? { ...r, paid: true } : r)));
  };

  const removeOvertime = async (id) => {
    const ok = window.confirm("تأكيد حذف سجل الأوفر تايم؟");
    if (!ok) return;
    await apiFetch(`${API_BASE}/hr/overtime/${id}`, { method: "DELETE" });
    setOvertimeRecords((prev) => prev.filter((x) => x.id !== id));
    if (otEditingId === id) resetOvertimeForm();
  };

  const otHours = useMemo(() => hoursBetween(otFromTime, otToTime), [otFromTime, otToTime]);

  const resetOvertimeForm = () => {
    setOtEditingId(null);
    setOtEmployee(otEmployeeOptions[0] || "");
    setOtDate(dayjs().format("YYYY-MM-DD"));
    setOtFromTime("");
    setOtToTime("");
    setOtReason("");
  };

  const startEditOvertimeRecord = (r) => {
    setOtEditingId(r.id);
    setOtEmployee(normalizeEmployee(r.employee));
    setOtDate(r.date);
    setOtFromTime(r.fromTime || "");
    setOtToTime(r.toTime || "");
    setOtReason(r.reason || "");
    setAddFormType("overtime");
  };

  const addOvertimeRecord = async () => {
    if (!otEmployee) {
      window.alert("اختاري الموظف");
      return;
    }
    if (!otDate) {
      window.alert("اختاري التاريخ");
      return;
    }
    if (!otFromTime || !otToTime || otHours <= 0) {
      window.alert("حددي وقت البداية والنهاية بشكل صحيح (لازم إلى الساعة تكون بعد من الساعة)");
      return;
    }
    if (!otReason.trim()) {
      window.alert("لازم تكتبين السبب / الملاحظات");
      return;
    }

    try {
      setOtSaving(true);
      if (otEditingId) {
        const res = await apiFetch(`${API_BASE}/hr/overtime/${otEditingId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            employee: otEmployee,
            date: otDate,
            fromTime: otFromTime,
            toTime: otToTime,
            hours: otHours,
            reason: otReason.trim(),
          }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || "فشل تعديل الأوفر تايم");
        }

        setOvertimeRecords((prev) =>
          prev.map((r) =>
            r.id === otEditingId
              ? { ...r, employee: otEmployee, date: otDate, fromTime: otFromTime, toTime: otToTime, hours: otHours, reason: otReason.trim() }
              : r
          )
        );
      } else {
        const res = await apiFetch(`${API_BASE}/hr/overtime`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            employee: otEmployee,
            date: otDate,
            fromTime: otFromTime,
            toTime: otToTime,
            hours: otHours,
            reason: otReason.trim(),
            createdBy: currentUser,
          }),
        });

        const created = await res.json();

        if (!res.ok) {
          throw new Error(created.error || "فشل إضافة الأوفر تايم");
        }

        setOvertimeRecords((prev) => [
          { id: created.id, employee: otEmployee, date: otDate, fromTime: otFromTime, toTime: otToTime, hours: otHours, reason: otReason.trim(), paid: false, createdAt: new Date() },
          ...prev,
        ]);
      }
      resetOvertimeForm();
    } catch (err) {
      console.error("Error saving overtime record:", err);
      window.alert("حدث خطأ أثناء حفظ الأوفر تايم");
    } finally {
      setOtSaving(false);
    }
  };

  // ✅ حركة موحدة: إجازات + أوفر تايم + غياب بقائمة واحدة، كل وحدة متعلّمة
  // بنوعها لعرض التفاصيل المناسبة والأزرار المناسبة
  const unifiedTransactions = useMemo(() => {
    const leaveItems = requests.map((r) => ({ kind: "leave", sortKey: toDateSafe(r.createdAt)?.getTime() || 0, data: r }));
    const otItems = overtimeRecords.map((r) => ({ kind: "overtime", sortKey: toDateSafe(r.createdAt)?.getTime() || 0, data: r }));
    const absItems = absenceRecords.map((r) => ({ kind: "absence", sortKey: toDateSafe(r.createdAt)?.getTime() || 0, data: r }));
    return [...leaveItems, ...otItems, ...absItems].sort((a, b) => b.sortKey - a.sortKey);
  }, [requests, overtimeRecords, absenceRecords]);

  const filtered = useMemo(() => {
    let r = [...unifiedTransactions];

    // حركات الموظفين المخفيين ما تظهر إلا إذا "عرض المخفيين" مفعّل
    if (!showHidden) {
      r = r.filter((x) => !hiddenEmployees.includes(normalizeEmployee(x.data.employee)));
    }

    if (kindFilter !== "الكل") {
      r = r.filter((x) => x.kind === kindFilter);
    }

    if (employeeFilter !== "الكل") {
      r = r.filter((x) => normalizeEmployee(x.data.employee) === employeeFilter);
    }

    if (statusFilter !== "الكل") {
      // الفلتر بالحالة يخص الإجازات بس - الأوفر تايم والغياب ما إلها حالة موافقة/رفض
      r = r.filter((x) => x.kind === "leave" && x.data.status === statusFilter);
    }

    const q = searchText.trim().toLowerCase();
    if (q) {
      r = r.filter((x) => {
        const d = x.data;
        const haystack = [
          normalizeEmployee(d.employee),
          d.employee,
          d.date,
          d.reason,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    return r;
  }, [unifiedTransactions, employeeFilter, statusFilter, kindFilter, searchText, showHidden, hiddenEmployees]);

  // ✅ رصيد كل موظف (الإجازات المستحقة): تراكم شهري (يوم و6 ساعات = 1.75
  // يوم/شهر من 1/9/2026) ناقص الإجازات المعتمدة (الإجازات المستخدمة: أيام +
  // ساعات محوّلة بمعامل 0.125 يوم/ساعة) - الغياب لا يخصم
  const perEmployeeStats = useMemo(() => {
    const acc = {};
    EMPLOYEES.forEach((e) => {
      acc[e] = {
        fullDaysTaken: 0,
        hourlyHoursTaken: 0,
        absenceFullDays: 0,
        absenceHours: 0,
        overtimeHours: 0,
        overtimeHoursPaid: 0,
        overtimeWageTotal: 0,
        overtimeWagePaid: 0,
        totalRequests: 0,
      };
    });

    requests.forEach((r) => {
      const emp = normalizeEmployee(r.employee);
      if (!acc[emp]) return; // موظف غير ضمن قائمة إدارة الإجازات الحالية
      acc[emp].totalRequests += 1;
      if (r.status !== "approved") return;
      if (r.type === "hourly") acc[emp].hourlyHoursTaken += Number(r.hours || 0);
      else if (r.type === "fullDay") acc[emp].fullDaysTaken += 1;
    });

    absenceRecords.forEach((r) => {
      const emp = normalizeEmployee(r.employee);
      if (!acc[emp]) return;
      if (r.type === "hourly") acc[emp].absenceHours += Number(r.hours || 0);
      else acc[emp].absenceFullDays += 1;
    });

    overtimeRecords.forEach((r) => {
      const emp = normalizeEmployee(r.employee);
      if (!acc[emp]) return;
      const hours = Number(r.hours || 0);
      acc[emp].overtimeHours += hours;
      if (r.paid) acc[emp].overtimeHoursPaid += hours;
      const rate = OVERTIME_HOURLY_RATE[emp];
      if (rate) {
        const wage = hours * rate;
        acc[emp].overtimeWageTotal += wage;
        if (r.paid) acc[emp].overtimeWagePaid += wage;
      }
    });

    const accruedDays = monthsAccrued(dayjs()) * ACCRUAL_PER_MONTH;

    Object.keys(acc).forEach((emp) => {
      const s = acc[emp];
      s.accruedDays = accruedDays;
      // 🚫 الغياب لا يدخل بحساب الرصيد إطلاقاً - يُخصم من الراتب وليس من رصيد الإجازات
      s.totalBalance = Math.round(((BASE_LEAVE_BALANCE[emp] || 0) + accruedDays) * 100) / 100;
      const usedDays = s.fullDaysTaken + s.hourlyHoursTaken * HOURLY_LEAVE_DAY_FRACTION;
      s.remainingBalance = Math.round((s.totalBalance - usedDays) * 100) / 100;
    });

    return acc;
  }, [requests, absenceRecords, overtimeRecords]);

  const updateStatus = async (id, newStatus) => {
    await apiFetch(`${API_BASE}/hr/leave-requests/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: newStatus }),
    });

    setRequests((prev) =>
      prev.map((x) => (x.id === id ? { ...x, status: newStatus } : x))
    );
  };

  const removeRequest = async (id) => {
    const ok = window.confirm("تأكيد حذف الطلب؟");
    if (!ok) return;

    await apiFetch(`${API_BASE}/hr/leave-requests/${id}`, { method: "DELETE" });
    setRequests((prev) => prev.filter((x) => x.id !== id));
  };

  return (
    <Container dir="rtl" sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          mb={2}
        >
          <Typography variant="h4" fontWeight={900}>
            ✅ إدارة الموظفين (المدير)
          </Typography>

          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip label={`الإجمالي: ${filtered.length}`} color="primary" sx={{ fontWeight: 800 }} />
            <Chip
              label={`Pending: ${requests.filter((x) => x.status === "pending").length}`}
              color="warning"
              sx={{ fontWeight: 800 }}
            />
            <Chip
              label={`Approved: ${requests.filter((x) => x.status === "approved").length}`}
              color="success"
              sx={{ fontWeight: 800 }}
            />
            <Chip
              label={`Rejected: ${requests.filter((x) => x.status === "rejected").length}`}
              color="error"
              sx={{ fontWeight: 800 }}
            />
            <Chip
              clickable
              onClick={() => setHideDialogOpen(true)}
              label="👥 إخفاء / إظهار موظفين"
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
            {hiddenEmployees.length > 0 && (
              <Chip
                clickable
                onClick={() => setShowHidden((v) => !v)}
                label={showHidden ? "🙈 إخفاء المخفيين" : `👁️ عرض المخفيين (${hiddenEmployees.length})`}
                variant={showHidden ? "filled" : "outlined"}
                sx={{ fontWeight: 800 }}
              />
            )}
          </Box>
        </Box>

        {/* فلاتر */}
        <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="🔍 بحث (اسم الموظف، التاريخ، السبب)"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="اكتبي هنا للبحث في الحركات..."
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel sx={{ right: 0, left: "auto" }}>الموظف</InputLabel>
                <Select
                  value={employeeFilter}
                  onChange={(e) => setEmployeeFilter(e.target.value)}
                >
                  <MenuItem value="الكل">الكل</MenuItem>
                  {cardEmployees.map((e) => (
                    <MenuItem key={e} value={e}>
                      {e}{isHidden(e) ? " (مخفي)" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel sx={{ right: 0, left: "auto" }}>نوع الحركة</InputLabel>
                <Select
                  value={kindFilter}
                  onChange={(e) => setKindFilter(e.target.value)}
                >
                  <MenuItem value="الكل">الكل</MenuItem>
                  <MenuItem value="leave">إجازة</MenuItem>
                  <MenuItem value="overtime">أوفر تايم</MenuItem>
                  <MenuItem value="absence">غياب</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel sx={{ right: 0, left: "auto" }}>الحالة (إجازات فقط)</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="الكل">الكل</MenuItem>
                  <MenuItem value="pending">قيد المراجعة</MenuItem>
                  <MenuItem value="approved">موافقة</MenuItem>
                  <MenuItem value="rejected">مرفوض</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* بطاقات الرصيد لكل موظف */}
        <Grid container spacing={2} mb={2}>
          {cardEmployees.map((e) => {
            const hidden = isHidden(e);
            const showAbsenceOvertime = !NO_ABSENCE_OVERTIME.includes(e);
            const s = perEmployeeStats[e] || {
              fullDaysTaken: 0,
              hourlyHoursTaken: 0,
              absenceFullDays: 0,
              absenceHours: 0,
              overtimeHours: 0,
              overtimeHoursPaid: 0,
              overtimeWageTotal: 0,
              overtimeWagePaid: 0,
              totalBalance: BASE_LEAVE_BALANCE[e] || 0,
              remainingBalance: 0,
            };
            return (
              <Grid item xs={12} sm={6} md={4} key={e} sx={{ display: "flex" }}>
                <Paper sx={{ p: 2.3, borderRadius: 3, boxShadow: 2, width: "100%", height: "100%", boxSizing: "border-box", opacity: hidden ? 0.6 : 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={0.5}>
                    <Typography fontWeight={900} fontSize={18}>
                      {e}
                    </Typography>
                    <Chip
                      label={`الرصيد المتبقي: ${formatBalanceDaysHours(s.remainingBalance)}`}
                      color={s.remainingBalance < 0 ? "error" : "success"}
                      size="small"
                      sx={{ fontWeight: 900 }}
                    />
                  </Box>
                  <Typography fontSize={13} fontWeight={700} color="text.secondary">
                    الإجازات المستحقة: {formatBalanceDaysHours(s.totalBalance)}
                  </Typography>
                  <Typography fontSize={14} mb={showAbsenceOvertime ? 1 : 0}>
                    📦 الإجازات المستخدمة: <strong>{formatDaysHours(s.fullDaysTaken, s.hourlyHoursTaken)}</strong>
                  </Typography>
                  {showAbsenceOvertime && (
                  <>
                  <Typography fontSize={14}>
                    🚫 المجموع الكلي للغيابات: <strong>{formatDaysHours(s.absenceFullDays, s.absenceHours)}</strong>
                  </Typography>
                  <Box mt={0.5}>
                    <Typography fontSize={14}>
                      ⏱️ الأوفر تايم الكلي: <strong>{formatNum(s.overtimeHours)} ساعة</strong> (هذا فقط بالساعات)
                    </Typography>
                    <Typography fontSize={14}>
                      الأوفر تايم المدفوع: <strong>{formatNum(s.overtimeHoursPaid)} ساعة</strong>
                    </Typography>
                    <Typography fontSize={14} mb={OVERTIME_HOURLY_RATE[e] ? 1 : 0}>
                      الأوفر تايم المتبقي: <strong>{formatNum(s.overtimeHours - s.overtimeHoursPaid)} ساعة</strong>
                    </Typography>
                    {OVERTIME_HOURLY_RATE[e] && (
                      <TableContainer component={Box} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell align="center" sx={{ fontWeight: 900, fontSize: 11, borderLeft: "1px solid", borderColor: "divider" }}>
                                الأوفر تايم الكلي
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 900, fontSize: 11, borderLeft: "1px solid", borderColor: "divider" }}>
                                الأوفر تايم المدفوع
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 900, fontSize: 11 }}>
                                الأوفر تايم المتبقي
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell align="center" sx={{ fontWeight: 900, fontSize: 12.5, borderLeft: "1px solid", borderColor: "divider" }}>
                                {formatMoney(s.overtimeWageTotal)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 900, fontSize: 12.5, borderLeft: "1px solid", borderColor: "divider" }}>
                                {formatMoney(s.overtimeWagePaid)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 900, fontSize: 12.5 }}>
                                {formatMoney(s.overtimeWageTotal - s.overtimeWagePaid)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                  </>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* إضافة حركة (غياب / إجازة / أوفر تايم) - نموذج وحد ياخذ مساحة قليلة،
        يختارون النوع من الأزرار وبعدها يظهر النموذج المطلوب بس */}
        <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: 2, mb: 2 }}>
          <Typography fontWeight={900} mb={1.5}>
            ➕ إضافة حركة
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap" mb={addFormType ? 2 : 0}>
            <Button
              variant={addFormType === "absence" ? "contained" : "outlined"}
              color="error"
              onClick={() => setAddFormType(addFormType === "absence" ? null : "absence")}
              sx={{ fontWeight: 900 }}
            >
              🚫 إضافة غياب
            </Button>
            <Button
              variant={addFormType === "leave" ? "contained" : "outlined"}
              onClick={() => setAddFormType(addFormType === "leave" ? null : "leave")}
              sx={{ fontWeight: 900 }}
            >
              🏖️ إضافة إجازة
            </Button>
            <Button
              variant={addFormType === "overtime" ? "contained" : "outlined"}
              onClick={() => {
                if (addFormType === "overtime") {
                  resetOvertimeForm();
                  setAddFormType(null);
                } else {
                  setAddFormType("overtime");
                }
              }}
              sx={
                addFormType === "overtime"
                  ? { fontWeight: 900, bgcolor: "#ef6c00", "&:hover": { bgcolor: "#e65100" } }
                  : { fontWeight: 900, color: "#ef6c00", borderColor: "#ef6c00" }
              }
            >
              ⏱️ {otEditingId ? "تعديل أوفر تايم" : "إضافة أوفر تايم"}
            </Button>
          </Box>

          {addFormType === "absence" && (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ right: 0, left: "auto" }}>الموظف</InputLabel>
                  <Select value={absEmployee} onChange={(e) => setAbsEmployee(e.target.value)}>
                    {absEmployeeOptions.map((e) => (
                      <MenuItem key={e} value={e}>{e}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="التاريخ"
                  value={absDate}
                  onChange={(e) => setAbsDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <RadioGroup
                  row
                  value={absType}
                  onChange={(e) => setAbsType(e.target.value)}
                >
                  <FormControlLabel value="fullDay" control={<Radio size="small" />} label="يوم كامل" />
                  <FormControlLabel value="hourly" control={<Radio size="small" />} label="ساعات" />
                </RadioGroup>
              </Grid>

              {absType === "hourly" && (
                <>
                  <HourRangeFields
                    fromValue={absFromTime}
                    toValue={absToTime}
                    onFromChange={setAbsFromTime}
                    onToChange={setAbsToTime}
                  />
                  {absHours > 0 && (
                    <Grid item xs={12} sm={3}>
                      <Chip color="info" label={`المجموع: ${formatNum(absHours)} ساعة`} />
                    </Grid>
                  )}
                </>
              )}

              <Grid item xs={12} sm={absType === "hourly" ? 9 : 6}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="الملاحظات / السبب"
                  value={absReason}
                  onChange={(e) => setAbsReason(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  disabled={absSaving}
                  onClick={addAbsenceRecord}
                  sx={{ fontWeight: 900 }}
                >
                  ➕ إضافة
                </Button>
              </Grid>
            </Grid>
          )}

          {addFormType === "leave" && (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ right: 0, left: "auto" }}>الموظف</InputLabel>
                  <Select value={leaveEmployee} onChange={(e) => setLeaveEmployee(e.target.value)}>
                    {visibleEmployees.map((e) => (
                      <MenuItem key={e} value={e}>{e}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="التاريخ"
                  value={leaveDate}
                  onChange={(e) => setLeaveDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <RadioGroup
                  row
                  value={leaveType}
                  onChange={(e) => setLeaveType(e.target.value)}
                >
                  <FormControlLabel value="fullDay" control={<Radio size="small" />} label="يوم كامل" />
                  <FormControlLabel value="hourly" control={<Radio size="small" />} label="ساعات" />
                </RadioGroup>
              </Grid>

              {leaveType === "hourly" && (
                <>
                  <HourRangeFields
                    fromValue={leaveFromTime}
                    toValue={leaveToTime}
                    onFromChange={setLeaveFromTime}
                    onToChange={setLeaveToTime}
                  />
                  {leaveHours > 0 && (
                    <Grid item xs={12} sm={3}>
                      <Chip color="info" label={`المجموع: ${formatNum(leaveHours)} ساعة`} />
                    </Grid>
                  )}
                </>
              )}

              <Grid item xs={12} sm={leaveType === "hourly" ? 9 : 6}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="الملاحظات / السبب"
                  value={leaveReason}
                  onChange={(e) => setLeaveReason(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={leaveSaving}
                  onClick={addLeaveRecord}
                  sx={{ fontWeight: 900 }}
                >
                  ➕ إضافة
                </Button>
              </Grid>
            </Grid>
          )}

          {addFormType === "overtime" && (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ right: 0, left: "auto" }}>الموظف</InputLabel>
                  <Select value={otEmployee} onChange={(e) => setOtEmployee(e.target.value)}>
                    {(otEmployeeOptions.includes(otEmployee) || !otEmployee ? otEmployeeOptions : [...otEmployeeOptions, otEmployee]).map((e) => (
                      <MenuItem key={e} value={e}>{e}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="التاريخ"
                  value={otDate}
                  onChange={(e) => setOtDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <HourRangeFields
                fromValue={otFromTime}
                toValue={otToTime}
                onFromChange={setOtFromTime}
                onToChange={setOtToTime}
              />

              {otHours > 0 && (
                <Grid item xs={12} sm={3}>
                  <Chip color="info" label={`المجموع: ${formatNum(otHours)} ساعة`} />
                </Grid>
              )}

              <Grid item xs={12} sm={otHours > 0 ? 9 : 12}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="الملاحظات / السبب"
                  value={otReason}
                  onChange={(e) => setOtReason(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={otEditingId ? 6 : 3}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{ fontWeight: 900, bgcolor: "#ef6c00", "&:hover": { bgcolor: "#e65100" } }}
                  disabled={otSaving}
                  onClick={addOvertimeRecord}
                >
                  {otSaving ? "جارٍ الحفظ..." : otEditingId ? "💾 حفظ التعديل" : "➕ إضافة"}
                </Button>
              </Grid>
              {otEditingId && (
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    disabled={otSaving}
                    onClick={resetOvertimeForm}
                    sx={{ fontWeight: 900 }}
                  >
                    إلغاء التعديل
                  </Button>
                </Grid>
              )}
            </Grid>
          )}
        </Paper>

        {/* قائمة الحركات الموحدة */}
        <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: 2 }}>
          <Typography fontWeight={900} mb={2}>
            📌 الحركات (إجازات + أوفر تايم + غياب)
          </Typography>

          {loading ? (
            <Alert severity="info">جارِ التحميل...</Alert>
          ) : filtered.length === 0 ? (
            <Alert severity="warning">لا توجد حركات حسب الفلترة</Alert>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {filtered.map(({ kind, data: r }) => {
                if (kind === "leave") {
                  return (
                    <Paper key={`leave_${r.id}`} sx={{ p: 2, borderRadius: 3, border: "1px solid #eee" }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip size="small" label="إجازة" color="primary" sx={{ fontWeight: 800 }} />
                          <Typography fontWeight={900}>
                            {normalizeEmployee(r.employee)} — {r.type === "hourly" ? "زمنية" : "يوم كامل"} — {r.date}
                          </Typography>
                        </Box>
                        <Chip label={STATUS_LABEL[r.status] || r.status} color={statusColor(r.status)} sx={{ fontWeight: 900 }} />
                      </Box>
                      <Box mt={1}>
                        {r.type === "hourly" ? (
                          <Typography>
                            ⏱️ من {r.fromTime} إلى {r.toTime} — ساعات: <strong>{r.hours}</strong>
                          </Typography>
                        ) : (
                          <Typography>📅 يوم كامل</Typography>
                        )}
                        <Typography sx={{ mt: 0.5 }}>📝 السبب: {r.reason}</Typography>
                        <Typography fontSize={12} color="text.secondary" sx={{ mt: 0.5 }}>
                          🗓️ تاريخ تقديم الطلب: {formatSubmittedAt(r.createdAt)}
                        </Typography>
                      </Box>
                      <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                        <Button variant="contained" color="success" onClick={() => updateStatus(r.id, "approved")} disabled={r.status === "approved"} sx={{ fontWeight: 900 }}>
                          ✅ Approve
                        </Button>
                        <Button variant="contained" color="error" onClick={() => updateStatus(r.id, "rejected")} disabled={r.status === "rejected"} sx={{ fontWeight: 900 }}>
                          ❌ Reject
                        </Button>
                        <Button variant="outlined" color="error" onClick={() => removeRequest(r.id)} sx={{ fontWeight: 900 }}>
                          🗑️ Delete
                        </Button>
                      </Box>
                    </Paper>
                  );
                }

                if (kind === "overtime") {
                  return (
                    <Paper key={`ot_${r.id}`} sx={{ p: 2, borderRadius: 3, border: "1px solid #eee" }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip size="small" label="أوفر تايم" sx={{ fontWeight: 800, bgcolor: "#ef6c00", color: "#fff" }} />
                          <Typography fontWeight={900}>
                            {normalizeEmployee(r.employee)} — {r.date}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          {OVERTIME_HOURLY_RATE[normalizeEmployee(r.employee)] && (
                            <Chip
                              label={`المبلغ: ${formatMoney(Number(r.hours || 0) * OVERTIME_HOURLY_RATE[normalizeEmployee(r.employee)])}`}
                              size="small"
                              sx={{ fontWeight: 800, bgcolor: "#e8f5e9", color: "#1b5e20" }}
                            />
                          )}
                          <Chip label={r.paid ? "مدفوع" : "غير مدفوع"} color={r.paid ? "success" : "warning"} sx={{ fontWeight: 900 }} />
                        </Box>
                      </Box>
                      <Box mt={1}>
                        <Typography dir="ltr" sx={{ textAlign: "right" }}>
                          ⏱️ من {formatTime12h(r.fromTime)} إلى {formatTime12h(r.toTime)} — ساعات: <strong>{formatNum(r.hours)}</strong>
                        </Typography>
                        {r.reason && <Typography sx={{ mt: 0.5 }}>📝 السبب: {r.reason}</Typography>}
                        <Typography fontSize={12} color="text.secondary" sx={{ mt: 0.5 }}>
                          🗓️ تاريخ تقديم الطلب: {formatSubmittedAt(r.createdAt)}
                        </Typography>
                      </Box>
                      <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                        {!r.paid && (
                          <Button variant="contained" color="success" onClick={() => markOvertimePaid(r.id)} sx={{ fontWeight: 900 }}>
                            ✅ Paid
                          </Button>
                        )}
                        <Button variant="outlined" onClick={() => startEditOvertimeRecord(r)} sx={{ fontWeight: 900 }}>
                          ✏️ تعديل
                        </Button>
                        <Button variant="outlined" color="error" onClick={() => removeOvertime(r.id)} sx={{ fontWeight: 900 }}>
                          🗑️ Delete
                        </Button>
                      </Box>
                    </Paper>
                  );
                }

                // absence
                return (
                  <Paper key={`abs_${r.id}`} sx={{ p: 2, borderRadius: 3, border: "1px solid #eee" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip size="small" label="غياب" color="error" sx={{ fontWeight: 800 }} />
                        <Typography fontWeight={900}>
                          {normalizeEmployee(r.employee)} — {r.type === "hourly" ? "زمني" : "يوم كامل"} — {r.date}
                        </Typography>
                      </Box>
                    </Box>
                    <Box mt={1}>
                      {r.type === "hourly" ? (
                        <Typography>
                          ⏱️ من {formatTime12h(r.fromTime)} إلى {formatTime12h(r.toTime)} — ساعات: <strong>{formatNum(r.hours)}</strong>
                        </Typography>
                      ) : (
                        <Typography>📅 يوم كامل</Typography>
                      )}
                      {r.reason && <Typography sx={{ mt: 0.5 }}>📝 السبب: {r.reason}</Typography>}
                      <Typography fontSize={12} color="text.secondary" sx={{ mt: 0.5 }}>
                        🗓️ تاريخ تقديم الطلب: {formatSubmittedAt(r.createdAt)}
                      </Typography>
                    </Box>
                    <Box mt={2}>
                      <Button variant="outlined" color="error" onClick={() => removeAbsence(r.id)} sx={{ fontWeight: 900 }}>
                        🗑️ Delete
                      </Button>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </Paper>
      </Box>

      {/* 👥 نافذة إخفاء/إظهار الموظفين - المدير يختار منها مباشرة بدل زر بكل بطاقة */}
      <Dialog open={hideDialogOpen} onClose={() => setHideDialogOpen(false)} dir="rtl" fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900 }}>👥 إخفاء / إظهار موظفين</DialogTitle>
        <DialogContent dividers>
          <Typography fontSize={13} color="text.secondary" mb={1}>
            الموظف المخفي ما يظهر بالواجهة، بس بياناته تبقى محفوظة
          </Typography>
          {EMPLOYEES.map((e) => (
            <Box key={e} display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
              <Typography fontWeight={800}>{e}</Typography>
              <FormControlLabel
                sx={{ m: 0 }}
                labelPlacement="start"
                label={isHidden(e) ? "مخفي" : "ظاهر"}
                control={
                  <Switch
                    checked={!isHidden(e)}
                    onChange={() => toggleEmployeeHidden(e)}
                    color="success"
                  />
                }
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHideDialogOpen(false)} sx={{ fontWeight: 900 }}>
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
